import axios from 'axios';
import { Formik, FormikHelpers } from 'formik';
import FileDownload from 'js-file-download';
import styled from 'styled-components';

import {
    ContractPriceListMessage,
    ContractPriceListStatus,
    PriceListActivationMode,
} from '../../../../types/ContractMessage';
import { ValidationResponse } from '../../../../types/ValidationResponse';
import { Button } from '../../../cdl/Button/Button';
import { Modal } from '../../../cdl/Modal/Modal';
import { Text } from '../../../cdl/Text/Text';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { downloadContractPriceList } from '../../../common/api/clients/contracts.api';
import { ButtonGroup } from '../../../common/buttons/ButtonGroup';
import { extractFileName } from '../../../common/helpers/extractFileName.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';
import { useContractPriceListActivate } from '../../../contracts/detail/hooks/useContractPriceListActivate';
import { useContractPriceListDeactivate } from '../../../contracts/detail/hooks/useContractPriceListDeactivate';
import { useContractPriceListUpdate } from '../../../contracts/detail/hooks/useContractPriceListUpdate';
import { PriceListEditForm } from '../../ContractForm/PriceListSection/PriceListEditForm';
import {
    mapPriceListValidationErrors,
    toPriceListRequestPayload,
} from '../../ContractForm/PriceListSection/priceListValidation.helpers';
import { PriceListFormValue } from '../../ContractForm/PriceListSection/types';

interface ContractEditPriceListModalProps {
    activationMode?: PriceListActivationMode;
    contractId: string;
    isOpen: boolean;
    onDismiss: () => void;
    priceList: ContractPriceListMessage;
}

interface EditPriceListFormValues {
    priceListActivationMode: PriceListActivationMode;
    priceLists: [PriceListFormValue];
}

const DownloadableFileName = styled.button`
    padding: 0;
    border: 0;
    background: transparent;
    color: ${theme.colors.accent.foreground};
    font: inherit;
    font-weight: ${theme.fontWeights[600]};
    text-align: left;
    cursor: pointer;
`;

const initialValues = (
    priceList: ContractPriceListMessage,
    activationMode?: PriceListActivationMode
): EditPriceListFormValues => ({
    priceLists: [
        {
            fileId: priceList.originalFileId,
            fileName: priceList.fileName ?? translate('contracts.priceLists.fileNameFallback'),
            validFrom: priceList.validFrom,
            validUntil: priceList.validUntil ?? null,
            activeOnApproval: priceList.activeOnApproval,
            initialStatus: priceList.status,
            status: priceList.status,
        },
    ],
    priceListActivationMode: activationMode ?? PriceListActivationMode.AUTO,
});

export const ContractEditPriceListModal = ({
    activationMode,
    contractId,
    isOpen,
    onDismiss,
    priceList,
}: ContractEditPriceListModalProps) => {
    const activatePriceListMutation = useContractPriceListActivate();
    const deactivatePriceListMutation = useContractPriceListDeactivate();
    const updatePriceListMutation = useContractPriceListUpdate();
    const { addToast, addErrorToast } = useToasts();

    const downloadPriceList = async () => {
        try {
            const response = await downloadContractPriceList(contractId, priceList.id);
            const fallbackFileName = priceList.fileName ?? translate('contracts.priceLists.fileNameFallback');

            FileDownload(
                response.data,
                extractFileName(response.headers['content-disposition'] || '', fallbackFileName)
            );
        } catch {
            addErrorToast(translate('global.genericToastError'));
        }
    };

    const handleSubmit = async (
        values: EditPriceListFormValues,
        { setErrors }: FormikHelpers<EditPriceListFormValues>
    ) => {
        const editedPriceList = values.priceLists[0];
        const shouldActivate =
            editedPriceList.status === ContractPriceListStatus.ACTIVE &&
            priceList.status !== ContractPriceListStatus.ACTIVE;
        const shouldDeactivate =
            editedPriceList.status !== ContractPriceListStatus.ACTIVE &&
            priceList.status === ContractPriceListStatus.ACTIVE;

        try {
            await updatePriceListMutation.mutateAsync({
                contractId,
                priceListId: priceList.id,
                priceList: toPriceListRequestPayload([editedPriceList], values.priceListActivationMode)[0],
            });

            if (shouldActivate) {
                await activatePriceListMutation.mutateAsync({ contractId, priceListId: priceList.id });
            }

            if (shouldDeactivate) {
                await deactivatePriceListMutation.mutateAsync({ contractId, priceListId: priceList.id });
            }

            addToast(translate('contracts.priceLists.edit.toast.success'));
            onDismiss();
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 400 && error.response.data) {
                setErrors(
                    mapPriceListValidationErrors<EditPriceListFormValues>(error.response.data as ValidationResponse, 0)
                );
                return;
            }

            addErrorToast(translate('contracts.priceLists.edit.toast.error'));
        }
    };

    return (
        <Formik
            initialValues={initialValues(priceList, activationMode)}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({ isSubmitting, submitForm }) => (
                <Modal isOpen={isOpen} onDismiss={onDismiss} size="large">
                    <Box padding={24} display="grid" rowGap={24}>
                        <Text variant="title">{translate('contracts.priceLists.edit.title')}</Text>

                        <Box
                            padding={16}
                            borderRadius={8}
                            border="1px solid"
                            borderColor="border.subtle"
                            display="flex"
                            flexDirection="column"
                        >
                            <PriceListEditForm
                                activationMode={activationMode ?? PriceListActivationMode.AUTO}
                                index={0}
                                isEdit
                                fileName={
                                    <DownloadableFileName type="button" onClick={downloadPriceList}>
                                        {priceList.fileName ?? translate('contracts.priceLists.fileNameFallback')}
                                    </DownloadableFileName>
                                }
                            />
                        </Box>

                        <ButtonGroup justifyContent="flex-end">
                            <Button type="button" onClick={onDismiss} disabled={isSubmitting}>
                                {translate('contracts.priceLists.cancel')}
                            </Button>
                            <Button
                                type="button"
                                emphasis="high"
                                onClick={() => submitForm()}
                                isLoading={
                                    isSubmitting ||
                                    activatePriceListMutation.isPending ||
                                    deactivatePriceListMutation.isPending
                                }
                            >
                                {translate('contracts.priceLists.save')}
                            </Button>
                        </ButtonGroup>
                    </Box>
                </Modal>
            )}
        </Formik>
    );
};
